import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { BroadcastEvents } from 'src/app/enums/broadcast-events';
import { ElementAttribute } from 'src/app/enums/element-attribute';
import { RestApiEndpoints } from 'src/app/enums/rest-api-endpoints';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { CredentialsService } from 'src/app/services/credentials.service';
import { RestApiService } from 'src/app/services/rest-api.service';

declare function openModalWithID(modalID: string): void;
declare function makeToast(message: string): void;
declare function cleanInput(inputString: string, shouldURLEncode: boolean): string;
declare function md5(string: string): string;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {

  isLoading: boolean = false;

  loginUsername: string = '';
  loginPassword: string = '';

  newPassword: string = '';
  confirmPassword: string = '';

  isPasswordVisible: boolean = false;
  isApplicationOnline: boolean = false;

  constructor(private location: Location,
    private router: Router,
    private restApiService: RestApiService,
    private credentialsService: CredentialsService,
    private broadcastService: BroadcastService) {

    this.isApplicationOnline = credentialsService.isApplicationOnline;
    this.broadcastService.on(BroadcastEvents.networkStateChanged).subscribe(() => {
        this.isApplicationOnline = credentialsService.isApplicationOnline;

        const networkStateToastMessage = this.credentialsService.isApplicationOnline ? "Die Anwendung is online" : "Die Anwendung is offline";
        makeToast(networkStateToastMessage);
    });

    if (location.isCurrentPathEqualTo("/login/contact")) {
      openModalWithID('contactModal');
    } else if (credentialsService.checkAuthentication()) {  
      this.loginSucceeded();
    }
  }

  loginButtonClicked() {
    if (!this.isApplicationOnline) {
      makeToast('Eine Anmeldung ist nicht möglich da die Anwendung offline ist');
      return;
    }

    if (!this.loginUsername) {
      makeToast('Bitte einen Usernamen eingeben');
      return;
    }

    if (!this.loginPassword) {
      makeToast('Bitte ein Passwort eingeben');
      return;
    }

    if (this.loginPassword.length < 6) {
      makeToast('Das Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    const postBody = {
      username: cleanInput(this.loginUsername, true),
      password: cleanInput(this.loginPassword, true)
    }

    this.loginUserWithPostBody(postBody);
  }

  changePasswordButtonClicked() {
    if (!this.isApplicationOnline) {
      makeToast('Passwort ändern ist nicht möglich da die Anwendung offline ist');
      return;
    }

    if (!this.loginUsername) {
      makeToast('Bitte einen Usernamen eingeben');
      return;
    }

    if (!this.loginPassword) {
      makeToast('Bitte ein Passwort eingeben');
      return;
    }

    if (!this.newPassword) {
      makeToast('Bitte ein neues Passwort eingeben');
      return;
    }

    if (this.newPassword.length < 8) {
      makeToast('Das neue Passwort muss mindestens 8 Zeichen haben');
      return;
    }

    if (this.newPassword.length < 8) {
      makeToast('Das neue Passwort muss mindestens 8 Zeichen haben');
      return;
    }

    if (this.newPassword.replace(/\D/g, '').length < 1) {
      makeToast('Das neue Passwort muss mindestens eine Zahl beinhalten');
      return;
    }

    if (this.newPassword.replace(/[^A-Z]/g, '').length < 1) {
      makeToast('Das neue Passwort muss mindestens einen Großbuchstaben beinhalten');
      return;
    }

    if (this.newPassword.replace(/\d|\w/g, '').length < 1) {
      makeToast('Das neue Passwort muss mindestens einen Sonderzeichen beinhalten');
      return;
    }

    if (this.newPassword != this.confirmPassword) {
      makeToast('Die Passwörter stimmen nicht überein');
      return;
    }

    const postBody = {
      username: cleanInput(this.loginUsername, true),
      old_password: cleanInput(this.loginPassword, true),
      new_password: cleanInput(this.newPassword, true)
    };

    this.isLoading = true;
    this.restApiService.sendAsyncRequestToEndpoint(RestApiEndpoints.passwortsicherheit, postBody, (success, response) => {
      this.isLoading = false;

      if (!success) {
        makeToast('Verbindungsfehler, bitte erneut versuchen');
        return;
      }

      if (!response.success) {
        makeToast('Fehler beim Ändern des Passworts');
        return;
      }

      const loginPostBody = {
        username: cleanInput(this.loginUsername, true),
        password: cleanInput(this.newPassword, true)
      };

      this.loginUserWithPostBody(loginPostBody);
    });
  }

  private loginUserWithPostBody(postBody: any) {
    if (!postBody) {
      makeToast('Anwendungsfehler, bitte erneut versuchen');
      return;
    }

    this.isLoading = true;
    this.restApiService.sendAsyncRequestToEndpoint(RestApiEndpoints.login, postBody, (success, response) => {
      this.isLoading = false;

      if (!success) {
        makeToast('Verbindungsfehler, bitte erneut versuchen');
        return;
      }

      if (!response.success) {
        makeToast('Username oder Passwort falsch');
        return;
      }

      if (!response.hasOwnProperty('payload')) {
        openModalWithID('change-password-modal');
        return;
      }

      this.credentialsService.userCredentials({
        sessionID: response.payload.id,
        sessionToken: response.payload.token
      });

      this.credentialsService.changeActiveUser({
        userImage: 'fbso.jpg',
        userName: response.payload.bestatter.bestatter,
        userRole: response.payload.bestatter.rolle_admin,
        userID: response.payload.bestatter.rolle_id,
        humanID: response.payload.bestatter.benutzer_id,
        humanName: response.payload.bestatter.benutzer_username
      });

      localStorage.setItem('bestatter', JSON.stringify(response.payload.bestatter));

      if (response.payload.bestatter.hasOwnProperty('status_aenderungen_senden') && response.payload.bestatter.status_aenderungen_senden && response.payload.bestatter.status_aenderungen_senden == 1) {
        localStorage.setItem('sendMail', JSON.stringify(true));
      } else {
        localStorage.setItem('sendMail', JSON.stringify(false));
      }

      if (response.payload.hasOwnProperty('standorte') && response.payload.standorte && response.payload.standorte.length > 0) {
        localStorage.setItem('standorte', JSON.stringify(response.payload.standorte));
      } else {
        localStorage.removeItem('standorte');
      }

      this.loginSucceeded()
    });
  }

  loginSucceeded() {
    const redirect = localStorage.getItem('redirect');
    if (redirect) {
      this.router.navigate([redirect.replace(/"/g, '')]);
    } else {
      this.router.navigate(['/dash']);
    }
  }

  togglePasswordVisibility() {
    const passwordField = this.credentialsService.parseElementBy(ElementAttribute.id, 'loginPassword');
    if (!passwordField) { return; }

    if (passwordField.type == 'password') {
      passwordField.type = 'text';
      this.isPasswordVisible = true;
    } else {
      passwordField.type = 'password';
      this.isPasswordVisible = false;
    }
  }

}
